import { TagInfo } from "@/apis/table/types";
import { TagSearchResultsItemV2 } from "@/apis/taxonomy/types";
import SearchResultItem from "@/components/common/search-result-item";
import { useMappingInfos } from "@/components/modals/mapping-info/hooks";
import { useTagging } from "@/hooks/mutations/use-tag";
import useRoleOfCell from "@/hooks/queries/use-role-of-cell";
import { useMappingStore } from "@/stores/mapping";
import { Button } from "@/styles/ui/button";
import { Loader2, Plus } from "lucide-react";
import { memo, useCallback } from "react";
import { toast } from "sonner";
import { useSelectItem } from "../detail/hooks";

type SearchTagListItemProps = {
  tag: TagSearchResultsItemV2;
  containerTag: TagSearchResultsItemV2 | null;
  isSelected?: boolean;
  originalRoleKey: string | null;
  onSelect: (
    tag: TagSearchResultsItemV2,
    containerTag: TagSearchResultsItemV2 | null,
  ) => void;
  onClickAfterAction?: () => void;
};

/**
 * 검색 결과 한 줄.
 *
 * 성능: 태그 추가에만 필요한 무거운 훅(useMappingInfos / useTagging /
 * useSelectItem / useRoleOfCell)은 아래 SelectedAddTagButton 안으로 옮겼다.
 * 그 영역은 선택된 항목에서만 렌더되므로(= shownContentWhenSelected), 훅도
 * 선택된 1개 항목에서만 실행된다. 목록의 나머지 항목은 라벨 렌더 + 선택 콜백만
 * 담당해 가볍다. (이전에는 200개 항목이 각각 useMappingInfos를 2번씩 호출해
 * 렌더가 수 초씩 걸렸다.)
 */
const SearchTagListItem = ({
  tag,
  containerTag,
  isSelected = false,
  originalRoleKey,
  onSelect,
  onClickAfterAction,
}: SearchTagListItemProps) => {
  const [tagPrefix, tagName] = tag.key.split("_");
  const isStandard = tagPrefix !== "entity";

  const handleClick = useCallback(async () => {
    onSelect(tag, containerTag);
  }, [onSelect, tag, containerTag]);

  return (
    <SearchResultItem
      id={tag.id}
      isStandard={isStandard}
      isSelected={isSelected}
      isSum={tag.isSum}
      label={tag.koLabel}
      prefix={tagPrefix}
      name={tagName}
      tagType={tag.type}
      handleClick={handleClick}
      className="w-full"
      shownContentWhenSelected={
        <SelectedAddTagButton
          tag={tag}
          containerTag={containerTag}
          originalRoleKey={originalRoleKey}
          onClickAfterAction={onClickAfterAction}
        />
      }
      contentContainerClassName="w-full"
    />
  );
};

/**
 * 선택된 항목에서만 마운트되는 "태그 추가" 영역.
 * 태그 추가에 필요한 무거운 훅들을 여기서 호출한다 → 선택된 1개 항목에 대해서만 실행된다.
 */
const SelectedAddTagButton = ({
  tag,
  containerTag,
  originalRoleKey,
  onClickAfterAction,
}: {
  tag: TagSearchResultsItemV2;
  containerTag: TagSearchResultsItemV2 | null;
  originalRoleKey: string | null;
  onClickAfterAction?: () => void;
}) => {
  const [tagPrefix, tagName] = tag.key.split("_");
  const [containerTagPrefix, containerTagName] =
    containerTag?.key.split("_") ?? [];

  const {
    workbookId,
    tableId,
    isLoading: addTagLoading,
  } = useMappingInfos({
    toToast: false,
  });
  const dimension = useMappingStore((state) => state.dimension.dimension);
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { data: getCellOfRoleData } = useRoleOfCell({
    workbookId: workbookId,
    tableId: tableId,
    cellAddress: selectedFact?.address,
  });

  const tagInfo: TagInfo = {
    name: tagName,
    label: tag.koLabel,
    prefix: tagPrefix,
    type: dimension === "LINE_ITEM" ? "LINE_ITEM" : "MEMBER",
    id: tag.id,
    parent: containerTag
      ? {
          id: containerTag.id,
          name: containerTagName,
          label: containerTag.koLabel,
          prefix: containerTagPrefix,
          type: dimension === "LINE_ITEM" ? "LINE_ITEMS" : "AXIS",
        }
      : null,
    roleKey: getCellOfRoleData?.defRole?.key ?? "",
  };
  const { handleClick } = useTagging({
    toast: {
      onSuccess: () => toast.success("태그가 추가되었습니다."),
      onError: () => toast.error("태그 추가에 실패했습니다."),
    },
  });

  const onSuccessCallback = () => {
    handleClick(tagInfo, false)();
    onClickAfterAction?.();
  };

  const { handleSelectItem, isLoading: selectItemLoading } = useSelectItem({
    parentKey: containerTag?.key ?? null,
    tagKey: tag.key,
    roleKey: getCellOfRoleData?.defRole.key ?? "",
    originalRoleKey: originalRoleKey,
    onSuccessCallback: onSuccessCallback,
  });

  if (!getCellOfRoleData) {
    return null;
  }

  return (
    <AddTagButton
      isLoading={selectItemLoading || addTagLoading}
      onClick={handleSelectItem}
    />
  );
};

const AddTagButton = ({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) => {
  return (
    <div className="flex flex-row gap-2 mt-2 justify-end items-center w-full">
      <Button
        className="rounded-[8px] h-[32px] py-2 px-3"
        onClick={(e) => {
          onClick();
        }}
        disabled={isLoading}
      >
        <>
          <div>
            {isLoading ? (
              <Loader2 className="w-[16px] h-[16px] animate-spin" />
            ) : (
              <Plus className="w-[16px] h-[16px] " />
            )}
          </div>
          <div className="caption-1-medium">태그 추가</div>
        </>
      </Button>
    </div>
  );
};

export default memo(SearchTagListItem);
